/**
 * In-SPA ICRC explorer data hooks for USDM / MMK.
 * Behavior parity with V1 useIcrcExplorer — new rewrite for frontend-v2.
 * Uses @motokomaker/ic-client + @icp-sdk; no explorer package imports.
 */
import { Actor, type HttpAgent } from "@icp-sdk/core/agent";
import { IDL } from "@icp-sdk/core/candid";
import { Principal } from "@icp-sdk/core/principal";
import {
	createHttpAgent,
	natToAmount,
	resolveTransferLedgerId,
} from "@motokomaker/ic-client";
import { useQuery } from "@tanstack/react-query";
import {
	fetchAccountTransactions,
	resolveIndexCanisterId,
	type IndexTokenId,
	type WalletTxRow,
} from "@/lib/icrc-history";
import { getIcConfig } from "@/lib/network";

export type ExplorerToken = "USDM" | "MMK";

export type TokenExplorerMeta = {
	name: string;
	symbol: string;
	decimals: number;
	fee: number;
	feeE8: bigint;
	supply: number;
	supplyE8: bigint;
	ledgerId: string;
	indexId: string | null;
};

export type TokenExplorerBalance = {
	amount: number;
	amountE8: bigint;
};

const metaIdl: IDL.InterfaceFactory = ({ IDL: I }) =>
	I.Service({
		icrc1_name: I.Func([], [I.Text], ["query"]),
		icrc1_symbol: I.Func([], [I.Text], ["query"]),
		icrc1_decimals: I.Func([], [I.Nat8], ["query"]),
		icrc1_fee: I.Func([], [I.Nat], ["query"]),
		icrc1_total_supply: I.Func([], [I.Nat], ["query"]),
		icrc1_balance_of: I.Func(
			[
				I.Record({
					owner: I.Principal,
					subaccount: I.Opt(I.Vec(I.Nat8)),
				}),
			],
			[I.Nat],
			["query"],
		),
	});

type MetaLedger = {
	icrc1_name: () => Promise<string>;
	icrc1_symbol: () => Promise<string>;
	icrc1_decimals: () => Promise<number>;
	icrc1_fee: () => Promise<bigint>;
	icrc1_total_supply: () => Promise<bigint>;
	icrc1_balance_of: (a: {
		owner: Principal;
		subaccount: [] | [Uint8Array];
	}) => Promise<bigint>;
};

async function anonymousAgent(): Promise<HttpAgent> {
	const config = getIcConfig();
	return createHttpAgent(config);
}

export function resolveExplorerLedgerId(token: ExplorerToken): string | null {
	const config = getIcConfig();
	const id = resolveTransferLedgerId(config, token);
	return id?.trim() || null;
}

export function explorerConfigured(token: ExplorerToken): boolean {
	return !!resolveExplorerLedgerId(token);
}

function isValidPrincipalText(text: string | null | undefined): text is string {
	if (!text || text.length < 10) return false;
	try {
		Principal.fromText(text.trim());
		return true;
	} catch {
		return false;
	}
}

export function useTokenExplorerMeta(token: ExplorerToken) {
	const ledgerId = resolveExplorerLedgerId(token);
	return useQuery({
		queryKey: ["v2-explorer", "meta", token, ledgerId],
		enabled: !!ledgerId,
		queryFn: async (): Promise<TokenExplorerMeta> => {
			const agent = await anonymousAgent();
			const actor = Actor.createActor<MetaLedger>(metaIdl, {
				agent,
				canisterId: ledgerId!,
			});
			const [name, symbol, decimals, fee, supply] = await Promise.all([
				actor.icrc1_name(),
				actor.icrc1_symbol(),
				actor.icrc1_decimals(),
				actor.icrc1_fee(),
				actor.icrc1_total_supply(),
			]);
			const dec = Number(decimals);
			// ic-client natToAmount assumes 8 decimals (USDM/MMK e8)
			return {
				name,
				symbol,
				decimals: dec,
				fee: natToAmount(fee),
				feeE8: fee,
				supply: natToAmount(supply),
				supplyE8: supply,
				ledgerId: ledgerId!,
				indexId: resolveIndexCanisterId(token as IndexTokenId),
			};
		},
		staleTime: 30_000,
	});
}

export function useTokenExplorerBalance(
	token: ExplorerToken,
	ownerText: string | null,
) {
	const ledgerId = resolveExplorerLedgerId(token);
	const valid = isValidPrincipalText(ownerText);
	return useQuery({
		queryKey: ["v2-explorer", "balance", token, ledgerId, ownerText],
		enabled: !!ledgerId && valid,
		queryFn: async (): Promise<TokenExplorerBalance> => {
			const owner = Principal.fromText(ownerText!.trim());
			const agent = await anonymousAgent();
			const actor = Actor.createActor<MetaLedger>(metaIdl, {
				agent,
				canisterId: ledgerId!,
			});
			const bal = await actor.icrc1_balance_of({
				owner,
				subaccount: [],
			});
			// Prefer live decimals from meta when available; default e8 for USDM/MMK
			return { amount: natToAmount(bal), amountE8: bal };
		},
	});
}

export function useTokenExplorerTxs(
	token: ExplorerToken,
	ownerText: string | null,
) {
	const ledgerId = resolveExplorerLedgerId(token);
	const indexId = resolveIndexCanisterId(token as IndexTokenId);
	const valid = isValidPrincipalText(ownerText);
	return useQuery({
		queryKey: ["v2-explorer", "txs", token, ledgerId, indexId, ownerText],
		enabled: !!ledgerId && !!indexId && valid,
		queryFn: async (): Promise<WalletTxRow[]> => {
			const owner = Principal.fromText(ownerText!.trim());
			const agent = await anonymousAgent();
			const page = await fetchAccountTransactions(
				agent,
				token as IndexTokenId,
				owner,
				40,
			);
			return page.rows;
		},
	});
}
